import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';
import Chip from '@mui/material/Chip';
import { useTranslation } from 'react-i18next';

import type { Post } from '@/lib/schemas/post';
import type { Resolution } from '@/lib/schemas/response';

interface PostBusinessMarkersProps {
  post: Post;
}

/**
 * "A business answered this, and here is where it got to."
 *
 * On the card itself rather than only inside the post, because the state of a
 * complaint is most of what a reader wants from a list: a BAD post that was
 * answered and fixed reads completely differently from one nobody replied to,
 * and opening thirty posts to find out which is which is not a feed.
 *
 * Icon and words, never colour alone (spec §13).
 */
export function PostBusinessMarkers({ post }: PostBusinessMarkersProps) {
  const { t } = useTranslation();

  const answered = post.officialResponses.length > 0;
  const state = latestState(post.resolutions);

  if (!answered && !state) return null;

  return (
    <>
      {answered && (
        <Chip
          size="small"
          variant="outlined"
          icon={<RecordVoiceOverRoundedIcon fontSize="small" aria-hidden />}
          label={t('business.response.label')}
          sx={{ color: 'business', borderColor: 'business' }}
        />
      )}

      {state && (
        <Chip
          size="small"
          variant="outlined"
          icon={<state.Icon fontSize="small" aria-hidden />}
          label={t(state.key)}
          sx={{ color: state.colour, borderColor: state.colour }}
        />
      )}
    </>
  );
}

interface Marker {
  key: string;
  colour: string;
  Icon: typeof CheckCircleRoundedIcon;
}

/**
 * The furthest a complaint got, across every business that answered it.
 *
 * "Acknowledged" earns no marker: the official-response chip beside it already
 * says somebody replied, and a second chip saying they had read it would be
 * two ways of saying the same thing.
 *
 * A resolution the author has not confirmed says "marked resolved", not
 * "resolved" — the difference is the whole point of the confirmation step.
 */
function latestState(resolutions: Resolution[]): Marker | null {
  const last = resolutions[resolutions.length - 1];
  if (!last) return null;

  switch (last.state) {
    case 'RESOLVED':
      return last.isConfirmed
        ? {
            key: 'business.resolution.states.RESOLVED',
            colour: 'good.text',
            Icon: CheckCircleRoundedIcon,
          }
        : {
            key: 'business.resolution.markedResolvedShort',
            colour: 'business',
            Icon: CheckCircleRoundedIcon,
          };
    case 'IN_PROGRESS':
      return {
        key: 'business.resolution.states.IN_PROGRESS',
        colour: 'business',
        Icon: HourglassTopRoundedIcon,
      };
    case 'WONT_FIX':
      return {
        key: 'business.resolution.states.WONT_FIX',
        colour: 'text.secondary',
        Icon: BlockRoundedIcon,
      };
    case 'ACKNOWLEDGED':
      return null;
  }
}
